import {useEffect, useState, useCallback} from 'react'
import {KTCardBody} from '../../../../_metronic/helpers'
import {
  getClassifiedCategories,
  deleteClassifiedCategory,
  restoreClassifiedCategory,
} from '../core/_requests'
import {ClassifiedCategory} from '../core/_models'
import {CategoryModal} from './CategoryModal'
import {ConfirmationModal} from '../components/ConfirmationModal'
import {toast} from 'react-toastify'
import {CategoriesListHeader} from './CategoriesListHeader'

const CategoriesTable = () => {
  const [categories, setCategories] = useState<ClassifiedCategory[]>([])
  const [loading, setLoading] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ClassifiedCategory | null>(null)
  const [showModal, setShowModal] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [itemsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [trashFilter, setTrashFilter] = useState('without_trash')

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: () => {},
    variant: 'danger' as 'danger' | 'primary' | 'warning',
  })
  const [deleting, setDeleting] = useState(false)

  const loadCategories = useCallback(
    async (page: number = 1) => {
      setLoading(true)
      try {
        let query = `page=${page}&limit=${itemsPerPage}`

        // Add search parameter
        if (searchTerm) {
          query += `&search=${encodeURIComponent(searchTerm)}`
        }

        // Add status filter
        if (statusFilter) {
          query += `&status=${statusFilter}`
        }

        // Add trash filter
        if (trashFilter) {
          query += `&filter_trashedstatus=${trashFilter}`
        }

        const response = await getClassifiedCategories(query)

        if (response.data) {
          setCategories(response.data)

          // Handle pagination data
          if (response.pagination) {
            setCurrentPage(response.pagination.page || 1)
            setTotalPages(response.pagination.last_page || 1)
            setTotalItems(response.pagination.totalDocs || 0)
          }
        }
      } catch (error) {
        toast.error('Failed to load categories')
      } finally {
        setLoading(false)
      }
    },
    [itemsPerPage, searchTerm, statusFilter, trashFilter]
  )

  useEffect(() => {
    loadCategories(currentPage)
  }, [currentPage, loadCategories])

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    if (currentPage === 1) {
      loadCategories(1)
    } else {
      setCurrentPage(1)
    }
  }, [searchTerm, statusFilter, trashFilter])

  const handleEdit = (category: ClassifiedCategory) => {
    setEditingCategory(category)
    setShowModal(true)
  }

  const handleDelete = (category: ClassifiedCategory) => {
    const isTrashed = category.deletedAt !== null && category.deletedAt !== undefined

    if (isTrashed) {
      // Hard delete confirmation (permanent deletion)
      setConfirmModalConfig({
        title: 'Permanent Delete Category',
        message:
          'Are you sure you want to permanently delete this category from the database?\n\n⚠️ WARNING: This action cannot be undone!',
        confirmText: 'Delete Permanently',
        variant: 'danger',
        onConfirm: async () => {
          setDeleting(true)
          try {
            await deleteClassifiedCategory(category.id as string, false)
            setShowConfirmModal(false)
            toast.success('Category permanently deleted from database')
            loadCategories(currentPage)
          } catch (error: any) {
            setShowConfirmModal(false)
            toast.error(error.response?.data?.message || 'Failed to permanently delete category')
          } finally {
            setDeleting(false)
          }
        },
      })
    } else {
      // Soft delete confirmation (move to trash)
      setConfirmModalConfig({
        title: 'Delete Category',
        message: 'Are you sure you want to delete this category? It will be moved to trash.',
        confirmText: 'Move to Trash',
        variant: 'danger',
        onConfirm: async () => {
          setDeleting(true)
          try {
            await deleteClassifiedCategory(category.id as string, false)
            setShowConfirmModal(false)
            toast.success('Category moved to trash successfully')
            loadCategories(currentPage)
          } catch (error: any) {
            // Check if error is due to existing subcategories
            if (error.response?.data?.message?.includes('Cannot delete category')) {
              const errorMessage = error.response.data.message
              setShowConfirmModal(false)

              // Show cascade confirmation
              setTimeout(() => {
                setConfirmModalConfig({
                  title: 'Force Delete Category',
                  message: `${errorMessage}\n\nDo you want to force delete this category along with all its subcategories and posts?\n\n⚠️ WARNING: This action cannot be undone!`,
                  confirmText: 'Force Delete',
                  variant: 'danger',
                  onConfirm: async () => {
                    setDeleting(true)
                    try {
                      await deleteClassifiedCategory(category.id as string, true)
                      setShowConfirmModal(false)
                      toast.success('Category and all related data moved to trash successfully')
                      loadCategories(currentPage)
                    } catch (cascadeError: any) {
                      setShowConfirmModal(false)
                      toast.error(
                        cascadeError.response?.data?.message || 'Failed to delete category'
                      )
                    } finally {
                      setDeleting(false)
                    }
                  },
                })
                setShowConfirmModal(true)
              }, 100)
            } else {
              setShowConfirmModal(false)
              toast.error(error.response?.data?.message || 'Failed to delete category')
            }
          } finally {
            setDeleting(false)
          }
        },
      })
    }
    setShowConfirmModal(true)
  }

  const handleRestore = async (category: ClassifiedCategory) => {
    if (!category.id) {
      toast.error('Invalid category selected')
      return
    }

    try {
      await restoreClassifiedCategory(category.id as string)
      toast.success('Category restored successfully')
      loadCategories(currentPage)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to restore category')
    }
  }

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page)
    }
  }

  const renderPaginationButtons = () => {
    const buttons: JSX.Element[] = []
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <a className='page-link' onClick={() => handlePageChange(i)} style={{cursor: 'pointer'}}>
            {i}
          </a>
        </li>
      )
    }

    return buttons
  }

  return (
    <>
      <CategoriesListHeader
        onSearchChange={setSearchTerm}
        onStatusChange={setStatusFilter}
        onTrashChange={setTrashFilter}
        onReload={() => loadCategories(currentPage)}
      />
      <KTCardBody className='py-4'>
        <div className='table-responsive'>
          <table className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'>
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                <th className='min-w-125px'>Name</th>
                <th className='min-w-125px'>Icon</th>
                <th className='min-w-200px'>Description</th>
                <th className='min-w-100px'>Status</th>
                <th className='text-end min-w-100px'>Actions</th>
              </tr>
            </thead>
            <tbody className='text-gray-600 fw-bold'>
              {loading ? (
                <tr>
                  <td colSpan={5} className='text-center'>
                    Loading...
                  </td>
                </tr>
              ) : categories.length > 0 ? (
                categories.map((category) => {
                  const isTrashed = category.deletedAt !== null && category.deletedAt !== undefined
                  return (
                    <tr key={category.id} className={isTrashed ? 'table-danger' : ''}>
                      <td>
                        <span className='text-gray-800 fw-bold'>{category.name}</span>
                        {isTrashed && (
                          <span className='badge badge-light-danger ms-2'>Trashed</span>
                        )}
                      </td>
                      <td>
                        <span style={{fontSize: '24px'}}>{category.icon}</span>
                      </td>
                      <td>
                        {category.description ? (
                          <div
                            className='text-muted'
                            style={{maxWidth: '280px'}}
                            dangerouslySetInnerHTML={{__html: category.description ?? ''}}
                          />
                        ) : (
                          <span className='text-muted'>-</span>
                        )}
                      </td>
                      <td>
                        {category.status === 'Active' ? (
                          <span className='badge badge-light-success'>Active</span>
                        ) : (
                          <span className='badge badge-light-danger'>Inactive</span>
                        )}
                      </td>
                      <td className='text-end'>
                        {!isTrashed && (
                          <button
                            className='btn btn-sm btn-light btn-active-light-primary me-2'
                            onClick={() => handleEdit(category)}
                          >
                            Edit
                          </button>
                        )}
                        {isTrashed ? (
                          <>
                            <button
                              className='btn btn-sm btn-light-primary me-2'
                              onClick={() => handleRestore(category)}
                            >
                              Restore
                            </button>
                            <button
                              className='btn btn-sm btn-danger'
                              onClick={() => handleDelete(category)}
                              title='Permanently delete from database'
                            >
                              Delete Permanently
                            </button>
                          </>
                        ) : (
                          <button
                            className='btn btn-sm btn-warning'
                            onClick={() => handleDelete(category)}
                            title='Move to trash'
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={5} className='text-center'>
                    No categories found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className='row'>
            <div className='col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start'>
              <div className='dataTables_info'>
                Showing {categories.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{' '}
                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
              </div>
            </div>
            <div className='col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end'>
              <div className='dataTables_paginate'>
                <ul className='pagination'>
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <a
                      className='page-link'
                      onClick={() => handlePageChange(currentPage - 1)}
                      style={{cursor: currentPage === 1 ? 'not-allowed' : 'pointer'}}
                    >
                      Previous
                    </a>
                  </li>
                  {renderPaginationButtons()}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <a
                      className='page-link'
                      onClick={() => handlePageChange(currentPage + 1)}
                      style={{cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'}}
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </KTCardBody>

      {showModal && (
        <CategoryModal
          category={editingCategory}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false)
            loadCategories(currentPage)
          }}
        />
      )}

      <ConfirmationModal
        show={showConfirmModal}
        title={confirmModalConfig.title}
        message={confirmModalConfig.message}
        confirmText={confirmModalConfig.confirmText}
        confirmVariant={confirmModalConfig.variant}
        onConfirm={confirmModalConfig.onConfirm}
        onCancel={() => {
          setShowConfirmModal(false)
          setDeleting(false)
        }}
        loading={deleting}
      />
    </>
  )
}

export {CategoriesTable}
